import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Icon } from "@/shared/components";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { PHONE_MAX_WIDTH, TAP_TARGET_PRIMARY, mediaPhone } from "@/shared/theme/breakpoints";
import { useNotificationsFeatureFlag } from "../hooks/useNotificationsFeatureFlag";
import { useNotifications } from "../hooks/useNotifications";
import { dismissFeatureIntro, hasDismissedFeatureIntro } from "../lib/featureIntro";
import { resolveNotificationCtaPath } from "../lib/ctaPath";
import { NotificationFeatureIntro } from "./NotificationFeatureIntro";
import { NotificationPanel } from "./NotificationPanel";
import { NotificationToast } from "./NotificationToast";
import type { NotificationItem } from "@/api/notifications";
import { pressedDarken } from "@/shared/theme/pressed";

const BellButton = styled.button<{ $active?: boolean }>`
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 16px;
	border-radius: 50%;
	transition: all 0.3s ease;
	border: none;
	outline: none;
	background: ${(props) => (props.$active ? "#FFE5F0" : "transparent")};
	color: ${(props) => (props.$active ? "#F72586" : "#454545")};
	position: relative;

	&:hover {
		box-shadow: ${(props) => (props.$active ? "none" : "0px 4px 12px 0px #1559A50A")};
		background: ${(props) => (props.$active ? "#FFE5F0" : "#ffffff")};
	}

	&:focus-visible {
		box-shadow: 0 0 0 2px #f72586;
	}

	&:disabled {
		cursor: not-allowed;
		color: #b9b9b9;
		opacity: 0.5;
		background: transparent;
		box-shadow: none;
	}

	${mediaPhone`
		width: ${TAP_TARGET_PRIMARY}px;
		height: ${TAP_TARGET_PRIMARY}px;
		flex: 0 0 ${TAP_TARGET_PRIMARY}px;
		padding: 12px;
	`}

	${pressedDarken}
`;

const Badge = styled.span`
	position: absolute;
	top: 14px;
	right: 14px;
	min-width: 11px;
	height: 11px;
	padding: 0 3px;
	border-radius: 999px;
	background: #f72586;
	color: #fff;
	font-family: Poppins, sans-serif;
	font-size: 8px;
	font-weight: 600;
	line-height: 11px;
	text-align: center;

	${mediaPhone`
		top: 10px;
		right: 10px;
	`}
`;

export function NotificationBell() {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { notificationsEnabled, loading } = useNotificationsFeatureFlag();
	const notifications = useNotifications(notificationsEnabled);
	const [open, setOpen] = useState(false);
	const [showIntro, setShowIntro] = useState(false);
	const [bellEl, setBellEl] = useState<HTMLButtonElement | null>(null);
	// On phones the bell sits in the bottom bar, so popups open above it.
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const tooltipPlacement = isPhone ? "top" : "right";

	useEffect(() => {
		if (loading || !notificationsEnabled || !user?.uuid) {
			setShowIntro(false);
			return;
		}
		setShowIntro(!hasDismissedFeatureIntro(user.uuid));
	}, [loading, notificationsEnabled, user?.uuid]);

	useEffect(() => {
		if (!open) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setOpen(false);
				notifications.onPanelClose();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open, notifications]);

	if (loading || !notificationsEnabled) {
		return (
			<AppTooltip title="Notifications" placement={tooltipPlacement} arrow={false}>
				<BellButton type="button" disabled aria-label="Notifications unavailable">
					<Icon name="bell" size={24} />
				</BellButton>
			</AppTooltip>
		);
	}

	const unread = notifications.counts.totalUnread;

	const dismissIntro = () => {
		dismissFeatureIntro(user?.uuid);
		setShowIntro(false);
	};

	const handleToggle = async () => {
		if (showIntro) dismissIntro();
		if (open) {
			setOpen(false);
			notifications.onPanelClose();
			return;
		}
		setOpen(true);
		await notifications.onPanelOpen();
	};

	const handleItemClick = async (item: NotificationItem) => {
		await notifications.markRead(item.id);
		setOpen(false);
		notifications.onPanelClose();
		const to = resolveNotificationCtaPath(item.ctaPath);
		if (to) navigate(to);
	};

	const handleToastView = async (item: NotificationItem) => {
		await notifications.markRead(item.id);
		notifications.dismissToast();
		const to = resolveNotificationCtaPath(item.ctaPath);
		if (to) navigate(to);
	};

	return (
		<>
			<AppTooltip
				title={showIntro ? "" : "Notifications"}
				placement={tooltipPlacement}
				arrow={false}
				// On phones a tap leaves the hover tooltip up over the open panel; the panel's title says it.
				open={showIntro || (isPhone && open) ? false : undefined}
			>
				<BellButton
					ref={setBellEl}
					$active={open}
					type="button"
					onClick={() => void handleToggle()}
					aria-label="Open notifications"
				>
					<Icon name="bell" size={24} />
					{unread > 0 && <Badge>{unread > 99 ? "99+" : unread}</Badge>}
				</BellButton>
			</AppTooltip>
			{showIntro && !open && (
				<NotificationFeatureIntro anchorEl={bellEl} placement={tooltipPlacement} onDismiss={dismissIntro} />
			)}
			<NotificationPanel
				open={open}
				onClose={() => {
					setOpen(false);
					notifications.onPanelClose();
				}}
				counts={notifications.counts}
				items={notifications.items}
				loadingList={notifications.loadingList}
				loadingMore={notifications.loadingMore}
				nextCursor={notifications.nextCursor}
				error={notifications.error}
				onLoadMore={() => void notifications.loadMore()}
				onMarkAllRead={() => void notifications.markAllRead()}
				onItemClick={(item) => void handleItemClick(item)}
			/>
			{notifications.toastItem && (
				<NotificationToast
					item={notifications.toastItem}
					onView={(item) => void handleToastView(item)}
					onDismiss={notifications.dismissToast}
				/>
			)}
		</>
	);
}
